import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useState } from 'react'

import { toastContainerSchema } from '@/@types/schemas/toast-containers'
import { showError } from '@/components/toast'
import useTranslator from '@/hooks/translation'
import { NetworkError } from '@/services/fintalk/api/types/errors/NetworkError'
import {
  Dashboard,
  listDashboards,
} from '@/services/fintalk/services/fintalk-backoffice/dashboards/list'

type UseDashboardsState = 'loading' | 'default' | 'error'

const useDashboards = (botId: string) => {
  const [state, setState] = useState<UseDashboardsState>('loading')
  const [dashboards, setDashboards] = useState<Dashboard[]>([])
  const { getTranslation } = useTranslator()

  const loadDashboards = useCallback(async (botId: string) => {
    try {
      setState('loading')
      const dashboards = await listDashboards(botId)

      setState('default')
      setDashboards(dashboards)
    } catch (error) {
      if (error instanceof NetworkError) {
        setState('error')
        showError({
          title: getTranslation('dashboards.list.toast.networkError.title'),
          description: getTranslation(
            'dashboards.list.toast.networkError.description',
          ),
          containerId: toastContainerSchema.Enum.BOT_LAYOUT,
        })
        return
      }

      const eventId = Sentry.captureException(error)
      setState('error')
      showError({
        title: getTranslation('dashboards.list.toast.unknownError.title'),
        description:
          getTranslation('dashboards.list.toast.unknownError.description') +
          `\nEvent ID: ${eventId}`,
        containerId: toastContainerSchema.Enum.BOT_LAYOUT,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadDashboards(botId)
  }, [botId, loadDashboards])

  return {
    state,
    dashboards,
    reloadDashboards: loadDashboards,
  }
}

type UseDashboardsResult = ReturnType<typeof useDashboards>

export { useDashboards }
export type { UseDashboardsResult }
